<template>
  <div class="nav-form container-fluid">

    <div class="search-group " v-show="isSearchGroupShown">

      <div class="d-flex">
        <BFormInput ref="searchInput" v-model="search" type="text" required @blur="hideSearchResultsTimer()"
          :placeholder="$t('search_placeholder')" @keyup.enter="goSearch()" class="search-input"></BFormInput>

        <span class="input-bar d-block d-lg-none"></span>

        <BButton @click="goSearch()" class="search-btn d-none d-lg-block" variant="light" squared aria-label="Search">
          {{ $t("search_btn") }}
        </BButton>

        <a href="#" class="search-icon-wrapper d-block d-lg-none">
          <img src="@/esf_venlo_base/assets/svg/search-icon.svg" class="search-icon" width="150" alt="search-icon" />
        </a>

      </div>

      <div class="search-results" :class="{ show: isSearchResultsShown }">
        <div class="px-4">
          <span class="search--title">{{ $t("search_results") }}</span>
        </div>
        <ul class="search-suggestions" v-if="searchSuggestions && searchSuggestions.length > 0">
          <li v-for="suggestion in searchSuggestions" :key="suggestion.word" class="d-block">
            <a @click.prevent="goRoute('/search', suggestion.word)">
              {{ suggestion.word }}
            </a>
          </li>
        </ul>
        <ul class="recommended-products">
          <li v-for="recommendedProduct in recommendedProducts" :key="recommendedProduct.sku"
            class="d-flex justify-content-between px-3">
            <div>
              <RouterLink :to="{
                path: '/search',
                query: { q: `${search}` },
                hash: `#${recommendedProduct.sku}`
              }" class="product-name">
                {{ recommendedProduct.name }}
              </RouterLink>
              <template v-for="item in recommendedProduct.information_attributes">
                <span class="d-block"> <strong>{{ item.label }}:</strong>&nbsp;&nbsp;{{ item.value }} </span>
              </template>
            </div>
            <div class="icon-wrapper d-flex justify-content-center align-items-center">
              <div>
                <b-img v-if="checkIfProductIsInWishlist(recommendedProduct)" :src="FillBookmark"
                  @click="removeBookmark(recommendedProduct.sku)" :alt="recommendedProduct.name"></b-img>
                <b-img v-else :src="Bookmark" :alt="recommendedProduct.name"
                  @click="addToBookmark(recommendedProduct)"></b-img>
              </div>

              <div>
                <b-img v-if="isExistInQuote(recommendedProduct.sku)" :src="RequestFill" :alt="recommendedProduct.name"
                  @click="removeRequestQuoteProduct(recommendedProduct)"></b-img>
                <b-img v-else :src="Request" :alt="recommendedProduct.name"
                  @click="requestQuote(recommendedProduct)"></b-img>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getProductsBySearchPreview } from "@storefront/core/data-resolver/products";
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useDeviceDetection } from "@/esf_venlo_base/store/composable/useDeviceDetection";
import { useCartAndWishlist } from "@storefront/core/composable/useCartWishlist";
import FillBookmark from "@/esf_venlo_base/assets/svg/fill-bookmark.svg";
import Bookmark from "@/esf_venlo_base/assets/svg/bookmark.svg";
import RequestFill from "@/esf_venlo_base/assets/svg/request-fill.svg";
import Request from "@/esf_venlo_base/assets/svg/request.svg";
import { computed } from "vue";

const { wishlistItems } = useCartAndWishlist();
const search = ref("");
const output = ref(null);
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const searchTimer = ref(null);
const closeSignal = ref(null);
const searchInput = ref(null);
const searchSuggestions = ref([]);
const recommendedProducts = ref([]);
const isSearchResultsShown = ref(false);
const { isDesktop } = useDeviceDetection();
const isSearchGroupShown = ref(isDesktop ? true : false);
const getProducts = computed(() => store.getters["quote/getProducts"]);

const toggleSearch = async () => {
  isSearchGroupShown.value = !isSearchGroupShown.value;

  await nextTick();

  if (isSearchGroupShown.value && searchInput.value) {
    searchInput.value?.focus();
  }
};

const removeBookmark = async (sku) => {
  const found = wishlistItems.value.find((v) => v.product.sku === sku);

  if (!found) return

  const retval = await store.dispatch("user/removeProductFromWishlist", { id: found.id, store })
  await store.dispatch("user/loadUser", { store })

  if (retval) {
    const msg = {
      type: "success",
      title: t("bookmark"),
      text: t("remove_successfully")
    };
    store.dispatch("messages/sendMessage", { message: msg });
  }
}

const addToBookmark = async (product) => {
  const retaval = await store.dispatch("user/addProductToWishlist", { sku: product.sku, store: store })

  if ("wishlist" in retaval.data.data.addProductsToWishlist) {
    const msg = {
      type: "success",
      title: t("bookmark"),
      text: t("added_successfully")
    };
    await store.dispatch("user/loadUser", { store })
    store.dispatch("messages/sendMessage", { message: msg });
  }

};

const requestQuote = async (product) => {
  product.quantity = product.quantity || 1;

  const productObj = {
    sku: product.sku,
    quantity: product.quantity,
    additional_info: product.additional_info || '',
  }
  store.commit("quote/setProductDetail", { payload: productObj, store });

  const msg = {
    type: "success",
    title: t("request_product"),
    text: t("added_successfully")
  };
  store.dispatch("messages/sendMessage", { message: msg });

};

const removeRequestQuoteProduct = (product) => {
  store.commit("quote/removeQuoteRequestProduct", { payload: product, store })
}


const checkIfProductIsInWishlist = (product) => {
  const found = wishlistItems.value.find((v) => v.product.sku === product.sku);
  if (found) {
    return true;
  } else {
    return false;
  }
};

const isExistInQuote = (sku) => {
  if (!sku) return false;
  return getProducts.value.some(p => p.sku === sku);
}

const close = () => {
  isSearchGroupShown.value = false;
};

const showSearchResults = () => {
  isSearchResultsShown.value = true;
};

const hideSearchResults = () => {
  isSearchResultsShown.value = false;
};

const getSearchPreview = async () => {
  const controller = new AbortController();
  const { signal } = controller;
  closeSignal.value = controller;

  output.value = await getProductsBySearchPreview(search.value, signal);
  closeSignal.value = null;

  recommendedProducts.value = output.value.items;
  searchSuggestions.value = output.value.search_suggestions;
  showSearchResults();
};

const goRoute = (route, query = null) => {
  hideSearchResults();
  search.value = "";
  if (query != null) {
    router.push({ path: route, query: { q: query } });
  } else {
    router.push({ path: route });
  }
};

const goSearch = () => {
  hideSearchResults();
  if (search.value.trim().length > 0 && search.value.trim().length < 35 && !search.value.includes("/")) {
    clearTimeout(searchTimer.value);
    if (closeSignal.value !== null) closeSignal.value.abort();
    router.push({
      path: "/search",
      query: { q: search.value.trim() },
    });
  } else if (search.value.trim().length > 35 && !search.value.includes("/")) {
    const msg = {
      type: "danger",
      title: t("search_error"),
      text: t("searchname_too_long"),
    };
    store.dispatch("messages/sendMessage", { message: msg });
  } else if (search.value.includes("/")) {
    const msg = {
      type: "danger",
      title: t("search_error"),
      text: t("search_validation"),
    };
    store.dispatch("messages/sendMessage", { message: msg });
  }
};

const hideSearchResultsTimer = () => {
  return setTimeout(() => {
    search.value = "";
    hideSearchResults();
  }, 1000);
};

watch(search, () => {
  if (search.value.length > 2) {
    if (searchTimer.value !== null) {
      clearTimeout(searchTimer.value);
    }
    searchTimer.value = setTimeout(() => {
      if (closeSignal.value !== null) {
        closeSignal.value.abort();
      }
      getSearchPreview();
    }, 2000);
  } else {
    clearTimeout(searchTimer.value);
    if (closeSignal.value !== null) {
      closeSignal.value.abort();
    }
    hideSearchResults();
  }
});
</script>